import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { InputError } from '@common/components/input';
import Input from '@common/components/input/input';

import UserDataSC from './user-data.styled';
import { initialValues, validation } from './user-data.schema';
import userInterfaceStore from '@store/user-interface';
import providerApi from '@utils/api';
import { TRootState } from '@store/root-reducer';
import Checkbox from '@common/components/checkbox';
import axios, { AxiosResponse } from 'axios';
import StepButtons from '@components/step-buttons/step-buttons';
import { HilbiWidgetViews } from '@store/user-interface/type';
import { useIntl } from 'react-intl';

const UserData = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [vopUrl, setVopUrl] = useState('#');
  const { formatMessage } = useIntl();

  const { email, leadToken } = useSelector(
    (store: TRootState) => store.userInterface.form,
  );

  const { eventId, note } = useSelector(
    (store: TRootState) => store.userInterface.order,
  );

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_NEWS_URI}/rest/sk/documents`)
      .then((res: AxiosResponse<Array<any>>) => {
        const resultUrl =
          res.data.find((item: any) => item.name === 'vop')?.file || '#';
        setVopUrl(resultUrl);
      });
  }, []);

  const { values, errors, touched, handleBlur, handleChange, submitForm } =
    useFormik({
      initialValues: { ...initialValues, email },
      validationSchema: validation,
      onSubmit: async (values) => {
        // TODO try / catch

        setLoading(true);

        const leadRequestResult = await providerApi.custom('/lead/profile', {
          method: 'POST',
          data: {
            name: values.name,
            phoneNumber: values.phoneNumber,
            insurance: values.insuranceCompany,
            termsAndConditionsApproval: true,
            leadToken,
          },
        });

        const { name, phoneNumber, insuranceCompany } = values;
        const newLeadToken: string = leadRequestResult.data.leadToken;

        dispatch(
          userInterfaceStore.setUserForm({
            name,
            phoneNumber,
            insuranceCompany,
            leadToken: newLeadToken,
          }),
        );

        // create order
        const orderRequestResult = await providerApi.custom(
          '/e-calendar/e-visit/orders',
          {
            method: 'POST',
            data: {
              leadToken: newLeadToken,
              eventId,
              description: note,
            },
          },
        );

        setLoading(false);
        dispatch(userInterfaceStore.moveToNextStep());
      },
    });

  useEffect(() => {
    dispatch(
      userInterfaceStore.setNextStepDisabled({
        disabled: loading || Object.keys(errors).length > 0,
      }),
    );
  }, [loading, dispatch, errors]);

  return (
    <UserDataSC>
      <div className="header">
        <h4>Vaše údaje</h4>
      </div>
      <div className="content">
        <div className="inputs-wrapper">
          <label htmlFor="name">Meno a priezvisko</label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Ján Novák"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            className="custom-input"
            variation="round"
            hasError={Boolean(errors.name && touched.name)}
          />
          {errors.name && touched.name ? (
            <InputError error={errors.name} />
          ) : null}
          <label htmlFor="email">E-mail</label>
          <Input
            id="email"
            name="email"
            type="email"
            readOnly={true}
            placeholder="jan@novak.sk"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email as string}
            className="custom-input"
            variation="round"
          />
          <label htmlFor="phone">Telefón</label>
          <Input
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            placeholder="+421 XXX YYY ZZZ"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.phoneNumber}
            className="custom-input"
            variation="round"
            hasError={Boolean(errors.phoneNumber && touched.phoneNumber)}
          />
          {errors.phoneNumber && touched.phoneNumber ? (
            <InputError error={errors.phoneNumber} />
          ) : null}
          <label htmlFor="insuranceCompany">Poisťovňa</label>
          <Input
            id="insuranceCompany"
            name="insuranceCompany"
            type="text"
            placeholder="Union, VŠZP, Dôvera.."
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.insuranceCompany}
            className="custom-input"
            variation="round"
            hasError={Boolean(
              errors.insuranceCompany && touched.insuranceCompany,
            )}
          />
          {errors.insuranceCompany && touched.insuranceCompany ? (
            <InputError error={errors.insuranceCompany} />
          ) : null}
          <div className="custom-checkbox-wrapper">
            <Checkbox
              id="vop"
              name="vop"
              checked={values.vop}
              value="vop"
              onChange={handleChange}
              onBlur={handleBlur}
              hasError={Boolean(errors.vop && touched.vop)}
              children={
                <span>
                  Súhlasím so{' '}
                  <a
                    href={vopUrl}
                    target={vopUrl === '#' ? '_self' : '_blank'}
                    rel="noreferrer"
                  >
                    všeobecnými obchodnými podmienkami Hilbi a.s.
                  </a>
                </span>
              }
            />
          </div>
          {errors.vop && touched.vop ? (
            <InputError className="checkbox-error" error={errors.vop} />
          ) : null}
        </div>
      </div>
      <div className="footer">
        <StepButtons
          renderNextBtn={true}
          handleNextClick={submitForm}
          nextBtnLabel={formatMessage({ id: 'buttons.send' })}
          nextBtnLoading={loading}
          handlePrevClick={() =>
            dispatch(
              userInterfaceStore.setCurrentStep({
                step: HilbiWidgetViews.CheckEmail,
              }),
            )
          }
        />
      </div>
    </UserDataSC>
  );
};

export default UserData;
